<template>
  <section id="overall-trends">
    <article>
      <div class="row clearfix">
        <h1>Overall Trends</h1>
        <div class="sixcol">
          <h2>Key Revenue Insights</h2>
          <ul>
            <li>In 2019, industry revenue decreased by 4.8% y-o-y.</li>
            <li>In 2020, industry revenue fell by 69.2% y-o-y due to COVID-19 restrictions.</li>
            <li>Revenue has grown at a compound annual growth rate (CAGR) of 5.8% over the period from 2008 to 2019, whereas from 2008 to 2020 it reported a negative CAGR of 4.6%.</li>
            <li>TAS witnessed the highest y-o-y growth in revenue in Australia (by 18.8%) in 2019. None of the states witnessed any y-o-y increase in 2020 and ACT witnessed the largest decline (81.9%).</li>
            <li>Circus and Physical Theatre witnessed the highest y-o-y growth (102.7%) in 2019, among all the other categories. This was driven by Cirque du Soleil tours.</li>
            <li>In 2020, Ballet and Dance witnessed the largest y-o-y decline of 88.6% driven by COVID-19 restrictions and limited touring activity.</li>
          </ul>
          <h2>Key Attendance Insights</h2>
          <ul>
            <li>Total attendance declined y-o-y in 2019 by 7.5% (to 23.9m), followed by a 67.5% decline (to 7.8m) in 2020.</li>
            <li>Attendance has grown at a CAGR of 3.8% from 15.8m in 2008 to 23.9m in 2019. However, over the period 2008 to 2020, attendance has declined at a CAGR of 5.8%, driven by the significant impact of COVID-19 restrictions.</li>
          </ul>
        </div>
        <div class="sixcol last">
          <ul>
            <li>In 2019, Special Events witnessed the highest y-o-y increase in attendance (152.9%) amongst all the categories. The ACT experienced the highest y-o-y growth in attendance (6.4%) in Australia.</li>
            <li>In 2020, all categories and states and territories witnessed a decline in attendance, with Comedy (84.4%) and Victoria (79.1%) experiencing the highest y-o-y decline. This was due to bans on mass gatherings, border closures and density limits introduced as part of COVID-19 restrictions, and the extended lockdown in Victoria.</li>
          </ul>
          <h2>Key Average Ticket Price Insights</h2>
          <ul>
            <li>The average ticket price increased by 13.8%, from $76.60 in 2008 to $87.14 in 2020.</li>
            <li>The average ticket price grew at a CAGR of 1.1% over the period 2008 to 2020.</li>
            <li>The average ticket price decreased by 3.9% y-o-y in 2019, from $96.61 in 2018 to $92.89 in 2019. It further declined in 2020 by 6.2% to $87.14 in 2020.</li>
            <li>The highest y-o-y growth in average ticket price was observed in Theatre (69.1%) in 2019 and in Comedy (30.9%) in 2020. The increase in Theatre average ticket price was primarily due to the opening of <em>Harry Potter and the Cursed Child</em>. The increase in Comedy average ticket price was primarily due to tours of renowned comedians, including Jack Whitehall, Russell Brand and Carl Barron.</li>
            <li>Festivals (Contemporary Music) command the highest average ticket price among all the categories at $147.86 in 2019 and $142.33 in 2020.</li>
          </ul>
        </div>
      </div>
    </article>

    <figure class="chart">
      <div class="row clearfix">
        <h2>Total Revenue and Attendance</h2>
        <div class="chart-container">
          <highcharts class="graphcontainer" :options="chartOptions"></highcharts>
          <div class="nb">NB: 2018 figures have been updated since the previous Report was released to account for an error in the data received.</div>
        </div>
      </div>
    </figure>

    <div class="fullbg">
      <p class="caption">Photo: <em>Bangarra: 30 years of sixty five thousand</em>, Bangarra Dance Theatre, Photo Credit: Daniel Boud</p>
      <div class="overlay"></div>
    </div>

    <figure id="total-rev-att-table" class="data">
      <div class="row clearfix">
        <a href="#" data-table="total-rev-att-table" class="closetable" title="Close Table">Close
          Table</a>
        <h3>Total Revenue, Attendance and Average Ticket Price (2004—2016)</h3>
        <table cellspacing="0" cellpadding="0">
          <thead>
          <tr>
            <th valign="middle" width="10%"></th>
            <th valign="middle" width="15%">Revenue ($)</th>
            <th valign="middle" width="15%">Growth (%)</th>
            <th valign="middle" width="15%">Total Attendance*</th>
            <th valign="middle" width="15%">Growth (%)</th>
            <th valign="middle" width="15%">Average Ticket Price ($)**</th>
            <th valign="middle" width="15%">Growth (%)</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td valign="middle">2004</td>
            <td valign="middle" class="number">689599070.00</td>
            <td valign="middle" class="number">0.00</td>
            <td valign="middle" class="number">13477231.00</td>
            <td valign="middle" class="number">0.00</td>
            <td valign="middle" class="number">55.13</td>
            <td valign="middle" class="number">0.00</td>
          </tr>
          <tr>
            <td valign="middle">2005</td>
            <td valign="middle" class="number">834337206.00</td>
            <td valign="middle" class="number">21.00</td>
            <td valign="middle" class="number">15808790.00</td>
            <td valign="middle" class="number">17.30</td>
            <td valign="middle" class="number">60.62</td>
            <td valign="middle" class="number">10.00</td>
          </tr>
          <tr>
            <td valign="middle">2006</td>
            <td valign="middle" class="number">1158064526.00</td>
            <td valign="middle" class="number">38.80</td>
            <td valign="middle" class="number">19835756.00</td>
            <td valign="middle" class="number">25.50</td>
            <td valign="middle" class="number">64.08</td>
            <td valign="middle" class="number">5.70</td>
          </tr>
          <tr>
            <td valign="middle">2007</td>
            <td valign="middle" class="number">1228658664.00</td>
            <td valign="middle" class="number">6.10</td>
            <td valign="middle" class="number">20887365.00</td>
            <td valign="middle" class="number">5.30</td>
            <td valign="middle" class="number">66.03</td>
            <td valign="middle" class="number">3.10</td>
          </tr>
          <tr>
            <td valign="middle">2008</td>
            <td valign="middle" class="number">1061273304.00</td>
            <td valign="middle" class="number">-13.60</td>
            <td valign="middle" class="number">15823705.00</td>
            <td valign="middle" class="number">-24.20</td>
            <td valign="middle" class="number">76.60</td>
            <td valign="middle" class="number">16.00</td>
          </tr>
          <tr>
            <td valign="middle">2009</td>
            <td valign="middle" class="number">1083329949.00</td>
            <td valign="middle" class="number">2.10</td>
            <td valign="middle" class="number">15196773.00</td>
            <td valign="middle" class="number">-4.00</td>
            <td valign="middle" class="number">80.57</td>
            <td valign="middle" class="number">5.20</td>
          </tr>
          <tr>
            <td valign="middle">2010</td>
            <td valign="middle" class="number">1327805816.00</td>
            <td valign="middle" class="number">22.60</td>
            <td valign="middle" class="number">17241139.00</td>
            <td valign="middle" class="number">13.50</td>
            <td valign="middle" class="number">86.43</td>
            <td valign="middle" class="number">7.30</td>
          </tr>
          <tr>
            <td valign="middle">2011</td>
            <td valign="middle" class="number">1309187150.00</td>
            <td valign="middle" class="number">-1.40</td>
            <td valign="middle" class="number">17345720.00</td>
            <td valign="middle" class="number">0.60</td>
            <td valign="middle" class="number">85.99</td>
            <td valign="middle" class="number">-0.50</td>
          </tr>
          <tr>
            <td valign="middle">2012</td>
            <td valign="middle" class="number">1204883551.00</td>
            <td valign="middle" class="number">-8.00</td>
            <td valign="middle" class="number">16273730.00</td>
            <td valign="middle" class="number">-6.20</td>
            <td valign="middle" class="number">85.46</td>
            <td valign="middle" class="number">-0.60</td>
          </tr>
          <tr>
            <td valign="middle">2013</td>
            <td valign="middle" class="number">1478976893.00</td>
            <td valign="middle" class="number">22.70</td>
            <td valign="middle" class="number">17926626.00</td>
            <td valign="middle" class="number">10.20</td>
            <td valign="middle" class="number">92.16</td>
            <td valign="middle" class="number">7.80</td>
          </tr>
          <tr>
            <td valign="middle">2014</td>
            <td valign="middle" class="number">1507963952.00</td>
            <td valign="middle" class="number">2.00</td>
            <td valign="middle" class="number">18536434.00</td>
            <td valign="middle" class="number">3.40</td>
            <td valign="middle" class="number">91.57</td>
            <td valign="middle" class="number">-0.60</td>
          </tr>
          <tr>
            <td valign="middle">2015</td>
            <td valign="middle" class="number">1413208686.00</td>
            <td valign="middle" class="number">-6.30</td>
            <td valign="middle" class="number">18626457.00</td>
            <td valign="middle" class="number">0.50</td>
            <td valign="middle" class="number">86.34</td>
            <td valign="middle" class="number">-5.70</td>
          </tr>
          <tr>
            <td valign="middle">2016</td>
            <td valign="middle" class="number">1430399693.00</td>
            <td valign="middle" class="number">1.20</td>
            <td valign="middle" class="number">18782588.00</td>
            <td valign="middle" class="number">0.80</td>
            <td valign="middle" class="number">83.72</td>
            <td valign="middle" class="number">-3.00</td>
          </tr>
          <tr>
            <td valign="middle">2017</td>
            <td valign="middle" class="number">1884141882.00</td>
            <td valign="middle" class="number">31.70</td>
            <td valign="middle" class="number">23027550.00</td>
            <td valign="middle" class="number">22.60</td>
            <td valign="middle" class="number">90.59</td>
            <td valign="middle" class="number">8.20</td>
          </tr>
          <tr>
            <td valign="middle">2018</td>
            <td valign="middle" class="number">2162425399.00</td>
            <td valign="middle" class="number">14.80</td>
            <td valign="middle" class="number">26268788.00</td>
            <td valign="middle" class="number">14.10</td>
            <td valign="middle" class="number">99.03</td>
            <td valign="middle" class="number">9.30</td>
          </tr>
          </tbody>
        </table>
        <figcaption>
          <p>*Based on both paid and unpaid tickets</p>
          <p>** Average Ticket Prices are calculated based only on paid tickets</p>
        </figcaption>
      </div>
    </figure>
  </section>
</template>

<script>
/*eslint-disable */

export default {
  data () {
    return {
      chartOptions: {
        chart: {
          backgroundColor: 'transparent'
        },
        title: {
          text: ''
        },
        xAxis: [{
          categories: [2004, 2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020],
          labels: {
            style: {
              color: '#fff'
            }
          }
        }],
        yAxis: [{ // Primary yAxis
          min: 0,
          labels: {
            formatter () {
              if (this.value > 999) {
                return '$' + (this.value / 1000) + 'b'
              }
              else {
                return '$' + this.value + 'm'
              }
            },
            style: {
              color: '#fff'
            }
          },
          title: {
            text: 'Gross Revenue',
            style: {
              color: '#fff'
            }
          },
          opposite: true
        }, { // Secondary yAxis
          min: 0,
          labels: {
            format: '{value}m',
            style: {
              color: '#fff'
            }
          },
          title: {
            text: 'Total Attendance',
            style: {
              color: '#fff'
            }
          },
          opposite: false
        }],
        tooltip: {
          shared: true,
          formatter () {
            let revy, tooltipstring, avg;
            let rev = this.points[0];
            let att = this.points[1];
            let atty = att.y.toFixed(2) + 'm';
            let chart = this.points[0].series.chart;
            let categories = chart.xAxis[0].categories;
            let index = 0;
            while (this.x !== categories[index]) {
              index++;
            }
            for (let i=0; i<chart.series.length; i++) {
              let series = chart.series[i];
              avg = series.data[index].y;
            }
            if (rev.y > 1000) {
              revy = '$' + (rev.y / 1000).toFixed(2) + 'b'
            }
            else {
              revy = '$' + rev.y + 'm'
            }
            tooltipstring = this.x + '<br />';
            tooltipstring += '<span style="color:' + att.series.color + '">•</span> Attendance: ' + atty + '<br />'
            tooltipstring += '<span style="color:' + rev.series.color + '">•</span> Revenue: ' + revy + '<br />'
            tooltipstring += '<span>•</span> Avg. Ticket Price: $' + avg.toFixed(2)
            return tooltipstring
          }
        },
        legend: {
          layout: 'vertical',
          align: 'left',
          x: 120,
          verticalAlign: 'top',
          y: 100,
          floating: true,
          backgroundColor: '#fff'
        },
        series: [
          {
            name: 'Revenue',
            type: 'column',
            data: [689.599, 834.337, 1158.065, 1228.659, 1061.273, 1083.33, 1327.806, 1309.187, 1204.884, 1478.977, 1507.964, 1413.209, 1430.4, 1884.142, 2062.425, 1964.642, 605.97],
            color: '#3a89eb'
          },
          {
            name: 'Attendance',
            type: 'column',
            yAxis: 1,
            data: [13.477, 15.809, 19.836, 20.887, 15.824, 15.197, 17.241, 17.346, 16.274, 17.927, 18.536, 18.626, 18.783, 23.028, 25.79, 23.864, 7.762],
            color: '#d14967'
          },
          {
            name: 'Avg ticket Price',
            type: 'line',
            yAxis: 1,
            data: [55.13, 60.62, 64.08, 66.03, 76.6, 80.57, 86.43, 85.99, 85.46, 92.16, 91.57, 86.34, 83.72, 90.59, 96.61, 92.89, 87.14],
            color: '#cd1f46',
            showInLegend: false,
            visible: false
          }
        ]
      }
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.chart-container {
  position: relative;
  overflow-x: visible;
  .nb {
    position: absolute;
    color: #fff;
    bottom: -30px;
    right: 10px;
    font-size: 12px;
    font-style: italic;
    font-family: sans-serif;
  }
}
</style>
